import { useCallback, useState, useReducer, useEffect, useMemo } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { SeoContentView, StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { createAction, createReducer } from '@reduxjs/toolkit';

import {
  putVendorPreferences,
  putWeddingPreferences,
  receivedWeddingPreferences,
  updatedVendorPreferences,
} from '~/actions/accountPreferencesActions';
import { receivedBookedVendors } from '~/actions/bookedVendorsActions';
import {
  receivedUserSettings,
  UserSettingsObjectType,
} from '~/actions/couples/types/userSettingsActionTypes';
import { receivedBulkInquiryToStorefront } from '~/actions/types/couplesInquiriesActionTypes';
import { cardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import { doTrackStorefrontListViewed } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { useUserContext } from '~/contexts/UserContext';
import { fetchRecentlyViewed, fetchTopPicks } from '~/libs/client/api/account';
import { fetchStorefrontSearchResults } from '~/libs/client/api/storefronts';
import { getSeoContentBlockHtml } from '~/libs/universal/seo';
import { fetchVendorSearchResultsPageData } from '~/libs/universal/vendorSearch';
import { useAppDispatch } from '~/reducers';
import type {
  AccountVendorPreferencesView,
  AccountVendorView,
  AccountWeddingPreferencesView,
  SaveAccountWeddingPreferencesRequest,
  TaxonomyNodeView,
  UpdateAccountVendorPreferenceRequest,
} from '~/types/responseTypes';
import ApiService from '~/util/apiService';

import {
  buildSearchFilterTracking,
  buildSearchSortTracking,
} from '../../../util/buildSearchFilterTracking';
import { buildBackendVendorSearchRequestFromSearchProperties } from '../../../util/buildSearchRequest';
import createCanonicalUrl, { createSeoCanonicalUrl } from '../../../util/canonicalUrl';
import {
  getSelectedFacetOptionsFromVendorSearch,
  ParseVendorSearchUrlResult,
} from '../../../util/parseVendorSearchUrl';
import { areTopPicksAllowedForUserInLocation } from '../components/TopPicks/topPicksConstants';
import type { VendorSearchRequest, VendorSearchRequestType } from '../types/types';
import {
  DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES,
  RecentlyViewedByTaxonomy,
  SearchResultsPageSearchDependentProps,
  SearchResultPageSearchResult,
  SearchResultsPageContext,
  SearchResultsPageContextValues,
  ShouldInsertMiniQuizByTaxonomy,
  SearchResultsPageUserAndSearchDependentProps,
  SearchResultsPageStaticProps,
  SearchResultsPageUserDependentProps,
} from './SearchResultsPageContext';

export const getVendorTaxonomiesByKey = (taxonomyNodes: TaxonomyNodeView[]) => {
  return taxonomyNodes.reduce((lookup, taxonomy) => {
    return {
      ...lookup,
      [taxonomy.key]: taxonomy,
    };
  }, {} as Record<VendorTaxonomyKey, TaxonomyNodeView>);
};

const getSelectedSrpFromSearch = (vendorSearch: VendorSearchRequest) => ({
  taxonomyKey: vendorSearch.selectedTaxonomy?.key,
  // vendorMarketId: vendorSearch.selectedCity?.vendorMarket?.id ?? undefined,
  locationSlug: vendorSearch.selectedLocation?.slug,
});

/**
 * Action when the taxonomy or location has changed
 */
const searchResultsPageTaxonomyOrLocationChange = createAction<
  Omit<ParseVendorSearchUrlResult, 'selectedFacetOptions'> &
    SearchResultsPageSearchDependentProps &
    // recentlyViewed is computed from recentlyViewedByTaxonomy and loading is set automatically when this action is dispatched
    Omit<SearchResultsPageUserAndSearchDependentProps, 'recentlyViewed' | 'loading'> & {
      // Note: Can update a single category
      recentlyViewedByTaxonomy: RecentlyViewedByTaxonomy;
    }
>('SEARCH_RESULTS_PAGE_CHANGE');

/**
 * Action for when a filter or sort has been applied, but the taxonomy and location are the same
 */
const searchResultsPageFilterChange = createAction<
  Omit<ParseVendorSearchUrlResult, 'selectedFacetOptions'> & {
    searchResults: SearchResultPageSearchResult;
    seoContent: SeoContentView | null;
  }
>('SEARCH_RESULTS_FILTER_CHANGE');

/**
 * Action when the wedding preferences changes (in the side drawer).  Triggers
 * a top pick update
 */
const weddingPreferencesChange = createAction<AccountWeddingPreferencesView>(
  'WEDDING_PREFERENCES_CHANGE'
);

/**
 * Action when the vendor preferences changes (in the side drawer).  Triggers
 * a top pick update
 */
const vendorPreferencesChange = createAction<AccountVendorPreferencesView>(
  'VENDOR_PREFERENCES_CHANGE'
);

/**
 * Action when some of the search results are loaded async
 */

const loadedDeferredVendorCards = createAction<StorefrontCardView[]>(
  'LOADED_DEFERRED_VENDOR_CARDS'
);

/**
 * Updates top picks after a preference change
 */
const topPicksChange =
  createAction<{ topPicks: StorefrontCardView[]; showTopPicks: boolean }>('TOP_PICKS_CHANGE');

/**
 * Sets loading to true before executing searches
 */
const setLoading = createAction('SET_LOADING');

/**
 * The reducer is the source of truth for the page.  It is a combination of
 * initial values from the server, and updates from the user (dispatched through
 * various actions).
 */
const searchResultsPageState = createReducer<
  Omit<
    SearchResultsPageContextValues,
    'setVendorSearch' | 'updateWeddingPreferences' | 'updateVendorPreferences' | 'recentlyViewed'
  > & {
    recentlyViewedByTaxonomy: RecentlyViewedByTaxonomy;
  }
>({ ...DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES, recentlyViewedByTaxonomy: {} }, (builder) => {
  builder
    .addCase(searchResultsPageTaxonomyOrLocationChange, (state, action) => {
      const { recentlyViewedByTaxonomy, ...payload } = action.payload;

      return {
        ...state,
        ...payload,
        selectedFacetOptions: getSelectedFacetOptionsFromVendorSearch(action.payload.vendorSearch),
        recentlyViewedByTaxonomy: {
          ...state.recentlyViewedByTaxonomy,
          ...recentlyViewedByTaxonomy,
        },
        loading: false,
      };
    })
    .addCase(loadedDeferredVendorCards, (state, action) => {
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          deferredVendorLoad: null,
          vendors: [...state.searchResults.vendors, ...action.payload],
        },
      };
    })
    .addCase(searchResultsPageFilterChange, (state, action) => {
      return {
        ...state,
        ...action.payload,
        selectedFacetOptions: getSelectedFacetOptionsFromVendorSearch(action.payload.vendorSearch),
        loading: false,
      };
    })
    .addCase(weddingPreferencesChange, (state, action) => {
      return {
        ...state,
        weddingPreferences: action.payload,
      };
    })
    .addCase(vendorPreferencesChange, (state, action) => {
      const newVendorPreferences = [
        ...state.vendorPreferences.filter(
          (pref) => pref.taxonomyNodeId !== action.payload.taxonomyNodeId
        ),
        action.payload,
      ];
      return {
        ...state,
        vendorPreferences: newVendorPreferences,
      };
    })
    .addCase(topPicksChange, (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    })
    .addCase(setLoading, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
});

/**
 * Helpers to track product list views on page load and follow up searches.
 */
const trackVendorSearch = (
  vendorSearch: VendorSearchRequest,
  searchRequest: VendorSearchRequestType,
  storefrontList: StorefrontCardView[]
) => {
  const taxonomyKey = vendorSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey | undefined;

  const trackingFilters = buildSearchFilterTracking(
    searchRequest,
    vendorSearch.selectedLocation,
    vendorSearch.selectedFacets
  );

  const trackingSort = buildSearchSortTracking(
    vendorSearch.sorting,
    taxonomyKey,
    vendorSearch.selectedLocation
  );

  doTrackStorefrontListViewed({
    storefrontList,
    filters: trackingFilters,
    taxonomyKey,
    sorts: trackingSort ? [trackingSort] : [],
    listingShelfName: 'SEARCH_RESULTS_PAGE',
    includeTags: true,
  });
};

export type SearchResultsPageProviderProps = SearchResultsPageStaticProps &
  SearchResultsPageSearchDependentProps &
  SearchResultsPageUserDependentProps & {
    storefrontToInquiryMap: Record<string, string>;

    /**
     * Does the user have a venue budget?  If not, we'll insert the budget quiz
     * on the venue SRP.
     */
    hasVenueBudget: boolean;

    /**
     * We hold out a certain percentage of users from seeing featured vendors
     */
    isInHoldoutGroupExperiment: boolean;

    /**
     * Vendors should never be held out from seeing featured vendors
     */
    identifiedAsVendor: boolean;

    /**
     * SRP A/B test
     */
    isInPreAuthSrpTest: boolean;

    /**
     * Static (page load time) lookup of if the user should see the mini-quiz by category
     */
    shouldInsertMiniQuiz: ShouldInsertMiniQuizByTaxonomy;

    /**
     * The user's account vendor records, if any. Indicates which categories
     * are booked, which we need to determine mini-quiz insertion dynamically.
     */
    accountVendors: AccountVendorView[];

    /**
     * Lookup of recently viewed vendors by taxonomy, or for all taxonomies by the empty string
     *
     * This is populated on load for the current category (or all categories) and updated
     * as the search changes.
     */
    recentlyViewedByTaxonomy: RecentlyViewedByTaxonomy;

    /**
     * User settings loaded on the server to stick in redux on page load.
     */
    userSettings: UserSettingsObjectType;

    parsedUrl: ParseVendorSearchUrlResult;

    searchResults: SearchResultPageSearchResult;

    showTopPicks: boolean;
    topPicks: StorefrontCardView[];
  };

export const SearchResultsPageProvider: React.FC<SearchResultsPageProviderProps> = ({
  children,
  facetsByVendorType,
  userSettings,
  accountVendors,
  weddingPreferences,
  vendorPreferences,
  storefrontToInquiryMap,
  hasVenueBudget,
  shouldInsertMiniQuiz,
  identifiedAsVendor,
  isInHoldoutGroupExperiment,
  searchResults,
  parsedUrl,
  ...value
}) => {
  const userContext = useUserContext();

  const [state, dispatch] = useReducer(searchResultsPageState, {
    ...value,
    ...parsedUrl,
    searchResults,
    weddingPreferences,
    vendorPreferences,
    facetsByVendorType,
    loading: false,
  });

  const appDispatch = useAppDispatch();
  useEffectOnce(() => {
    // Hydrate redux.  We could also do this on the server side by pre-initializing the store
    appDispatch(receivedUserSettings(userSettings));
    appDispatch(receivedBookedVendors(accountVendors));
    if (weddingPreferences) {
      appDispatch(receivedWeddingPreferences(weddingPreferences));
    }
    dispatch(receivedBulkInquiryToStorefront(storefrontToInquiryMap));
  });

  /**
   * One-time effect on load to load additional storefront cards on the client.
   *
   * Also, trigger the initial product list viewed tracking
   */
  useEffectOnce(() => {
    let loadOrNoOp: Promise<StorefrontCardView[]> = Promise.resolve([]);

    if (searchResults.deferredVendorLoad) {
      const { offset, limit } = searchResults.deferredVendorLoad;

      const searchReq = buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: parsedUrl.vendorSearch,
        // These are used for the limit calculation but we have an offset/limit
        // from the asyc load and we're going to override.
        shouldInsertBudgetQuiz: false,
        shouldInsertMiniQuiz: false,
        identifiedAsVendor,
        isInHoldoutGroupExperiment,
      });
      searchReq.offset = offset;
      searchReq.limit = limit;

      loadOrNoOp = fetchStorefrontSearchResults(searchReq)
        .then((additionalVendorsSearchResponse) => {
          dispatch(loadedDeferredVendorCards(additionalVendorsSearchResponse.entities));
          return additionalVendorsSearchResponse.entities;
        })
        .catch(() => []);
    }

    const vendorSearchRequestForInitialTracking =
      buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: parsedUrl.vendorSearch,
        shouldInsertBudgetQuiz: false,
        shouldInsertMiniQuiz: false,
        identifiedAsVendor,
        isInHoldoutGroupExperiment,
      });

    loadOrNoOp
      .then((additionalVendors) => {
        trackVendorSearch(parsedUrl.vendorSearch, vendorSearchRequestForInitialTracking, [
          ...searchResults.vendors,
          ...additionalVendors,
        ]);
      })
      .catch(() => null);
  });

  /**
   * Callback to update wedding preferences (side drawer for top picks)
   */
  const updateWeddingPreferences = useCallback(
    (weddingPreferencesUuid: string | null, values: SaveAccountWeddingPreferencesRequest) => {
      putWeddingPreferences(weddingPreferencesUuid, values)
        .then((newWeddingPreferences) => {
          dispatch(weddingPreferencesChange(newWeddingPreferences));
          appDispatch(receivedWeddingPreferences(newWeddingPreferences));
        })
        .catch(() => null);
    },
    [appDispatch]
  );

  /**
   * Callback to update per-category vendor preferences (side drawer for top picks)
   */
  const updateVendorPreferences = useCallback(
    (vendorPreferencesUuid: string | null, values: UpdateAccountVendorPreferenceRequest) => {
      putVendorPreferences(vendorPreferencesUuid, values)
        .then((newVendorPreferences: AccountVendorPreferencesView) => {
          dispatch(vendorPreferencesChange(newVendorPreferences));
          appDispatch(updatedVendorPreferences(newVendorPreferences));
        })
        .catch(() => null);
    },
    [appDispatch]
  );

  // Keep track of the SRP, when the location or category changes, we'll need
  // to fetch some new content and update the data initially provided by the server
  const [previousSrp, setPreviousSrp] = useState<{
    taxonomyKey: string | undefined;
    locationSlug: string | undefined;
  }>(getSelectedSrpFromSearch(parsedUrl.vendorSearch));

  /**
   * This is the main callback in the provider that triggers updates to the page
   * when filters / category / location / page / changes.
   */
  const setVendorSearch = useCallback(
    (nextSearch: VendorSearchRequest) => {
      // First, set loading so we can provide feedback to the couple (blur effect)
      dispatch(setLoading());

      // Determine if we are changing category or taxonomy
      const newSrp = getSelectedSrpFromSearch(nextSearch);

      // Compute new canonical URLS
      const { canonicalUrl } = createCanonicalUrl({
        vendorSearch: nextSearch,
        facetsByVendorType,
      });
      const { seoCanonicalUrl } = createSeoCanonicalUrl({
        vendorSearch: nextSearch,
        facetsByVendorType,
      });

      // Determine if we should be showing the budget quiz on the new page
      const shouldInsertBudgetQuiz: boolean =
        !hasVenueBudget && nextSearch.selectedTaxonomy?.key === VENUES_TAXONOMY_KEY;

      // Determine if we should be showing the mini-quiz on the new page
      const shouldInsertMiniQuizForCategory = Boolean(
        shouldInsertMiniQuiz[nextSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey]
      );

      // Build the API request to execute the search
      const vendorSearchRequest = buildBackendVendorSearchRequestFromSearchProperties({
        vendorSearch: nextSearch,
        shouldInsertBudgetQuiz,
        shouldInsertMiniQuiz: shouldInsertMiniQuizForCategory,
        identifiedAsVendor,
        isInHoldoutGroupExperiment,
      });

      // Construct a promise that will run the search.  We are either changing
      // filters or we are changing category/location.  Both of those need to
      // execute the search, but changing category/location fetches a bunch more data
      const executeSearch: () => Promise<SearchResultPageSearchResult> = () => {
        return fetchStorefrontSearchResults(vendorSearchRequest)
          .then((searchResponse) => ({
            totalHits: searchResponse.totalHits,
            offset: searchResponse.offset,
            vendors: searchResponse.entities,
            shouldInsertMiniQuiz: shouldInsertMiniQuizForCategory,
            shouldInsertBudgetQuiz,
            deferredVendorLoad: null,
          }))
          .then((result) => {
            // Trigger track product list viewed for the new results
            trackVendorSearch(nextSearch, vendorSearchRequest, result.vendors);
            return result;
          });
      };

      // Construct a promise to the search (and any other required fetches)
      let promise: Promise<any>;
      if (
        // Determine if category or location changed
        previousSrp?.taxonomyKey !== newSrp.taxonomyKey ||
        previousSrp?.locationSlug !== newSrp.locationSlug
      ) {
        const topPickProps = {
          weddingPreferences,
          vendorPreferences,
          userSettings,
          userContext,
          selectedTaxonomy: nextSearch.selectedTaxonomy,
        };

        const showTopPicks = areTopPicksAllowedForUserInLocation(topPickProps);

        // Category or location changed, we need to re-fetch quite a bit of data
        promise = Promise.all([
          // Fetch SEO meta-data
          fetchVendorSearchResultsPageData(nextSearch, facetsByVendorType, ApiService),

          // Grab the top picks
          showTopPicks
            ? fetchTopPicks({
                taxonomyKey: topPickProps.selectedTaxonomy.key,
                vendorMarketId:
                  nextSearch.selectedLocation?.storefrontSearchPayload?.vendorMarketId ?? null,
              })
            : Promise.resolve([]),

          // Get recently viewed
          userContext && !userContext?.is_guest
            ? fetchRecentlyViewed({
                taxonomyKey: nextSearch?.selectedTaxonomy?.key,
                vendorMarketId:
                  nextSearch.selectedLocation?.storefrontSearchPayload?.vendorMarketId ?? null,
              })
            : Promise.resolve([]),

          // Run the search
          executeSearch(),
        ]).then(
          ([
            [newSupplied, adjacentVendors, relatedLinks, newRealWeddings, expertAdvice, seoContent],
            topPicks,
            recentlyViewed,
            newSearchResults,
          ]) => {
            // Update the page with new data
            dispatch(
              searchResultsPageTaxonomyOrLocationChange({
                vendorSearch: nextSearch,
                canonicalUrl,
                seoCanonicalUrl,
                supplied: newSupplied,
                adjacentVendors,
                relatedLinks,
                realWeddings: newRealWeddings,
                expertAdvice,
                seoContent,
                showTopPicks,
                topPicks,
                recentlyViewedByTaxonomy: {
                  [nextSearch.selectedTaxonomy?.key ?? '']: recentlyViewed,
                },
                searchResults: newSearchResults,
              })
            );
          }
        );
      } else {
        // Much simpler when only filters change, just execute the search
        promise = Promise.all([
          executeSearch(),
          getSeoContentBlockHtml(nextSearch, ApiService),
        ]).then(([newSearchResults, seoContent]) => {
          dispatch(
            searchResultsPageFilterChange({
              vendorSearch: nextSearch,
              canonicalUrl,
              seoCanonicalUrl,
              searchResults: newSearchResults,
              seoContent,
            })
          );
        });
      }

      // Run the search (and related fetches) then record what the last category/taxonomy we searched was
      promise.finally(() => {
        setPreviousSrp(newSrp);
      });
    },
    [
      facetsByVendorType,
      hasVenueBudget,
      identifiedAsVendor,
      isInHoldoutGroupExperiment,
      previousSrp?.taxonomyKey,
      previousSrp?.locationSlug,
      shouldInsertMiniQuiz,
      userContext,
      userSettings,
      vendorPreferences,
      weddingPreferences,
    ]
  );

  // Compute new top picks
  // Note: This could probably be a useMemo
  useEffect(() => {
    const topPickProps = {
      weddingPreferences,
      vendorPreferences,
      userSettings,
      userContext,
      selectedTaxonomy: state.vendorSearch.selectedTaxonomy,
    };

    const showTopPicks = areTopPicksAllowedForUserInLocation(topPickProps);

    if (showTopPicks) {
      // fetchTopPicks({
      //   taxonomyKey: topPickProps.selectedTaxonomy.key,
      //   vendorMarketId: state.vendorSearch.selectedCity?.vendorMarket?.id ?? null,
      // })
      //   .then((topPicks) => {
      //     dispatch(topPicksChange({ topPicks, showTopPicks }));
      //   })
      //   .catch(() => {
      //     dispatch(topPicksChange({ topPicks: [], showTopPicks: false }));
      //   });
    } else {
      dispatch(topPicksChange({ topPicks: [], showTopPicks: false }));
    }
    // we only want to re-fetch top picks when wedding or vendor preferences change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.weddingPreferences, state.vendorPreferences]);

  // Compute recently viewed for the current taxonomy from the lookup
  const recentlyViewed = useMemo(() => {
    const taxonomyKey = (state.vendorSearch.selectedTaxonomy?.key || '') as
      | SearchableVendorTaxonomyKey
      | '';
    return (state.recentlyViewedByTaxonomy[taxonomyKey] || []).map(cardViewToVendorCardVendor);
  }, [state.recentlyViewedByTaxonomy, state.vendorSearch.selectedTaxonomy?.key]);

  return (
    <SearchResultsPageContext.Provider
      value={{
        ...state,
        setVendorSearch,
        updateWeddingPreferences,
        updateVendorPreferences,
        recentlyViewed,
      }}
    >
      {children}
    </SearchResultsPageContext.Provider>
  );
};
